import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Select, Tag } from 'antd';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import InsuranceTable from 'components/PPAOnboarding/InsuranceTable';
import commonS from '../../s.module.less';
import s from './s.module.less';
import { PaymentMethodOptions } from 'constants/ppaOnboarding';
import useGetPPAServiceSpecialtyList from 'hooks/useGetPPAServiceSpecialtyList';
import useGetSettingData from 'hooks/useGetSettingData';
import useGetPPAServicePriceConfig from 'hooks/useGetPPAServicePriceConfig';
import { EnumFields, SettingEnum } from 'types/enumerationData';
import { EServiceType, ServerKlarityService, TKlarityServiceServerTableItem, InsuranceSetting } from 'types/common';
import { INSURANCE_TYPE_CASH, INSURANCE_TYPE_INSURANCE, INSURANCE_TYPE_CASH_AND_INSURANCE } from 'constants/common';
import { PPAServicePriceConfig } from 'types/ppaOnboarding';
import { saveProviderKlarityService } from 'api/klarityService';
import { getUserId } from 'utils/localstore';
import { isMobile } from 'utils/common';

interface Props {
    initValue?: TKlarityServiceServerTableItem[] | null;
    onSave?: () => void;
    // Authoritative specialty from onboarding service/detail. When provided, it takes
    // precedence over initValue[0] (whose first row may belong to a different specialty).
    specialty?: string;
}

const ServicesCard: React.FC<Props> = (props) => {
    const { initValue, onSave, specialty: specialtyProp } = props;
    const providerId = getUserId();
    const [form] = Form.useForm();
    const [specialtyList] = useGetPPAServiceSpecialtyList();
    const [initServicePriceConfig, __, fetchInitConfig] = useGetPPAServicePriceConfig();
    const [allStates] = useGetSettingData(EnumFields.STATE);
    const [servicePriceConfig, setServicePriceConfig] = useState<PPAServicePriceConfig[]>();
    const specialtyValue = Form.useWatch('specialty', form);
    const paymentMethodValue = Form.useWatch('paymentMethod', form);
    const serviceStatesValue = Form.useWatch('serviceStates', form);
    const insuranceTableRef = useRef<{ getServerKlarityServiceData: () => ServerKlarityService }>(null);

    // 为了处理外部change，导致insuranceTable数据被恢复成initial状态，临时的解决方案
    // 在外部任何会手动修改insuranceTable数据的地方，都设置isEdit为true
    // 比如 select state
    const [isEdit, setIsEdit] = useState(false);

    const stateOptions = useMemo(() => {
        if (!allStates) return [];
        return (allStates as SettingEnum[])?.map((state) => ({
            value: state.dictKey,
            label: state.content,
        }));
    }, [allStates]);

    const specialty = specialtyProp || initValue?.[0]?.serviceSpecialtyName;
    const services =
        initValue?.filter(
            (item) => item.serviceSpecialtyName === specialty && item.serviceType === EServiceType.INIT,
        ) || [];
    const isSelfPay = services.some((item) => item.cashPay);
    const isInsurancePay = services.some((item) => item.insurancePay || (item.insurances?.length || 0) > 0);
    const paymentMethod = isSelfPay
        ? isInsurancePay
            ? INSURANCE_TYPE_CASH_AND_INSURANCE
            : INSURANCE_TYPE_CASH
        : INSURANCE_TYPE_INSURANCE;
    const insuranceSettings = services
        .map((item) => item.state)
        .reduce((acc: Record<string, InsuranceSetting>, state) => {
            const service = services.find((item) => item.state === state);
            if (!service) return acc;
            const carriers = Array.from(new Set(service.insurances?.map((item) => item.carrier)));
            acc[state] = {
                state,
                carriers: carriers.map((carrier) => ({
                    name: carrier,
                    plans: service.insurances
                        ?.filter((item) => item.carrier === carrier)
                        ?.map((item) => item.plan || ''),
                })),
                duration: service.duration,
                cashPrice: service.patientCost,
                insurancePay: !!service.insurancePay,
                cashPay: !!service.cashPay,
            };
            return acc;
        }, {});

    console.log('insuranceSettings', insuranceSettings);

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue({
                specialty,
                paymentMethod,
                serviceStates: services.map((item) => item.state),
            });
            setServicePriceConfig(
                services.map((item) => ({
                    duration: item.duration,
                    price: item.patientCost,
                    serviceType: item.serviceType,
                    specialty: item.serviceSpecialtyName,
                    state: item.state,
                })),
            );
        }
    }, [initValue, form, specialty]);

    const handleSave = async () => {
        try {
            await form.validateFields();
            const serverKlarityServiceData = insuranceTableRef.current?.getServerKlarityServiceData();
            console.log('serverKlarityServiceData', serverKlarityServiceData);
            if (!serverKlarityServiceData) {
                return;
            }
            const result = await saveProviderKlarityService(
                [
                    {
                        ...serverKlarityServiceData,
                        serviceType: EServiceType.INIT,
                        serviceSpecialtyId: String(
                            specialtyList.find((item) => item.specialtyName === specialtyValue)?.id || '',
                        ),
                        customizedServiceName: '',
                        providerId: providerId!,
                        serviceId: services[0]?.serviceId!,
                    },
                ],
                [],
                [],
            );
            console.log('result', result);
            await onSave?.();
        } catch (error) {
            console.error('Form save failed:', error);
            throw error;
        }
    };

    const renderEditing = () => {
        return (
            <Form form={form} layout="vertical" className={s.form}>
                <div className={commonS.row} style={{ flexDirection: isMobile() ? 'column' : 'row' }}>
                    <Form.Item
                        label="Specialty"
                        name="specialty"
                        className="flex-1"
                        rules={[{ required: true, message: 'Please select specialty' }]}
                    >
                        <Select
                            placeholder="Select specialty"
                            options={specialtyList.map((item) => ({
                                label: item.specialtyName,
                                value: item.specialtyName,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Payment method"
                        name="paymentMethod"
                        className="flex-1"
                        rules={[{ required: true, message: 'Please select payment method' }]}
                    >
                        <Select options={PaymentMethodOptions} />
                    </Form.Item>
                </div>
                <div className={commonS.row}>
                    <Form.Item
                        label="Service states"
                        name="serviceStates"
                        rules={[{ required: true, message: 'Please select service states' }]}
                        className={s.serviceStates}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select states"
                            options={stateOptions}
                            onChange={() => setIsEdit(true)}
                        />
                    </Form.Item>
                </div>

                <div className={commonS.row}>
                    <Form.Item
                        label="Appointment duration & payments"
                        name="appointmentDuration"
                        style={{ maxWidth: '100%' }}
                    >
                        <InsuranceTable
                            isEdit={isEdit}
                            paymentType={paymentMethodValue}
                            selectedSpecialty={specialtyValue}
                            states={serviceStatesValue}
                            servicePriceConfig={servicePriceConfig}
                            initialInsuranceSettings={insuranceSettings}
                            ref={insuranceTableRef}
                        />
                    </Form.Item>
                </div>
            </Form>
        );
    };

    const renderReview = () => {
        if (!initValue?.length) {
            return <div>Not provided</div>;
        }

        const serviceStates = services.map((item) => item.state);
        const paymentMethodLabel = PaymentMethodOptions.find((item) => item.value === paymentMethod)?.label;

        return (
            <div>
                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Specialty</div>
                        <div className={commonS.value}>{specialty || '-'}</div>
                    </div>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Payment method</div>
                        <div className={commonS.value}>{paymentMethodLabel || '-'}</div>
                    </div>
                </div>

                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Service states</div>
                        <div className={commonS.value}>{serviceStates?.length ? serviceStates.join('、') : '-'}</div>
                    </div>
                </div>

                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Appointment duration & payments</div>
                        <InsuranceTable
                            paymentType={(paymentMethod as any) || 'insurance-only'}
                            initialInsuranceSettings={insuranceSettings}
                            selectedSpecialty={specialty}
                            states={serviceStates}
                            servicePriceConfig={servicePriceConfig}
                            isReview
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <FormCard type="review" title="Services" hasContent onSave={handleSave}>
            {(isEditing) => (isEditing ? renderEditing() : renderReview())}
        </FormCard>
    );
};

export default ServicesCard;
